import type { FC, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Menu01Icon from '@untitled-ui/icons-react/build/esm/Menu01';
import type { Theme } from '@mui/material';
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  SvgIcon,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// import { Logo } from 'src/components/logo';
import { RouterLink } from 'src/components/router-link';
import { usePathname } from 'src/hooks/use-pathname';
import { useWindowScroll } from 'src/hooks/use-window-scroll';
import { paths } from 'src/paths';
import { TopNavItem } from './top-nav-item';
import { MobileNav } from './mobile-nav';

// import ChevronDownIcon from '@untitled-ui/icons-react/build/esm/ChevronDown'; // Or equivalent
// import ChevronUpIcon from '@untitled-ui/icons-react/build/esm/ChevronUp'; // Or equivalent
import { TopNavUseCasesMenu } from 'src/components/top-nav-use-cases-menu';

interface Item {
  disabled?: boolean;
  external?: boolean;
  popover?: ReactNode;
  path?: string;
  title: string;
  onClick?: (e: React.MouseEvent) => void;
}

const scrollToProducts = (e: React.MouseEvent) => {
  e.preventDefault();
  const element = document.getElementById('products-section');
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
};

const items: Item[] = [
  {
    title: 'Products',
    path: '#products-section',
    onClick: scrollToProducts
  },
  {
    title: 'Use Cases',
    // path: paths.components.index,
    popover: <TopNavUseCasesMenu />
  },
  {
    title: 'Docs',
    path: 'https://docs.swiftramp.in',
    external: true
  },
  {
    title: 'Company',
    path: 'https://platinx.exchange',
    external: true
  }
];

const TOP_NAV_HEIGHT: number = 64;

interface TopNavProps {
  onMobileNavOpen?: () => void;
}

export const TopNav: FC<TopNavProps> = () => {
  
  const pathname = usePathname();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [elevate, setElevate] = useState<boolean>(false);
  const offset = 64;
  const delay = 100;
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false);

  const handleWindowScroll = useCallback(
    (): void => {
      if (window.scrollY > offset) {
        setElevate(true);
      } else {
        setElevate(false);
      }
    },
    []
  );

  useWindowScroll({
    handler: handleWindowScroll,
    delay
  });

  const handleMobileNavOpen = (): void => {
    setIsMobileNavOpen(true);
  };

  const handleMobileNavClose = (): void => {
    setIsMobileNavOpen(false);
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          left: 0,
          position: 'fixed',
          right: 0,
          top: 0,
          pt: 2,
          zIndex: (theme) => theme.zIndex.appBar
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            backdropFilter: 'blur(6px)',
            backgroundColor: 'transparent',
            borderRadius: 2.5,
            boxShadow: 'none',
            transition: (theme) => theme.transitions.create('box-shadow, background-color', {
              easing: theme.transitions.easing.easeInOut,
              duration: 200
            }),
            ...(elevate && {
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.90),
              boxShadow: 8
            })
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{ height: TOP_NAV_HEIGHT }}
          >
            <Stack
              alignItems="center"
              direction="row"
              spacing={1}
              sx={{ flexGrow: 1 }}
            >
              <Stack
                alignItems="center"
                component={RouterLink}
                direction="row"
                display="inline-flex"
                href={paths.index}
                spacing={1}
                sx={{ textDecoration: 'none' }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    height: 60,
                    width: 'auto'
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/swift-ramp-logo.png"
                    alt="SwiftRamp"
                    sx={{
                      height: '100%',
                      width: 'auto',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                {/* {mdUp && (
                  <Box
                    sx={{
                      color: 'text.primary',
                      fontFamily: '\'Plus Jakarta Sans\', sans-serif',
                      fontSize: 18,
                      fontWeight: 700,
                      letterSpacing: '0.3px',
                      lineHeight: 2.5,
                      '& span': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    SwiftRamp
                  </Box>
                )} */}
              </Stack>
            </Stack>


            {mdUp && (
              <Stack
                alignItems="center"
                direction="row"
                spacing={2}
              >
                <Box
                  component="nav"
                  sx={{ height: '100%' }}
                >
                  <Stack
                    component="ul"
                    alignItems="center"
                    justifyContent="center"
                    direction="row"
                    spacing={1}
                    sx={{
                      height: '100%',
                      listStyle: 'none',
                      m: 0,
                      p: 0
                    }}
                  >
                    <>
                      {items.map((item) => {
                        const checkPath = !!(item.path && pathname);
                        const partialMatch = checkPath ? pathname.includes(item.path!) : false;
                        const exactMatch = checkPath ? pathname === item.path : false;
                        const active = item.popover ? partialMatch : exactMatch;

                        return (
                          <TopNavItem
                            active={active}
                            external={item.external}
                            key={item.title}
                            path={item.path}
                            popover={item.popover}
                            title={item.title}
                            onClick={item.onClick}
                          />
                        );
                      })}
                    </>
                  </Stack>
                </Box>
              </Stack>
            )}
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="flex-end"
              spacing={2}
              sx={{ flexGrow: 1 }}
            >
              <Button
                component={RouterLink}
                href={paths.dashboard.index}
                sx={{
                  color: 'text.primary',
                  fontWeight: 600
                }}
              >
                Log in
              </Button>
              <Button
                component="a"
                size={mdUp ? 'medium' : 'small'}
                href="https://cal.com/support-team-qsszsx"
                target="_blank"
                variant="contained"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  fontWeight: 600
                }}
              >
                Talk to us
              </Button>
              {!mdUp && (
                <IconButton onClick={handleMobileNavOpen}>
                  <SvgIcon fontSize="small">
                    <Menu01Icon />
                  </SvgIcon>
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Container>
      </Box>
      <MobileNav
        items={items}
        onClose={handleMobileNavClose}
        open={isMobileNavOpen}
      />
    </>
  );
};

TopNav.propTypes = {
  onMobileNavOpen: PropTypes.func
};
